import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowRight, X, Sparkles } from 'lucide-react';
import { apiService } from '../services/api';

/**
 * DraftFeedbackBanner - "Logbucheintrag vervollständigen" (Lernlogbuch 2.0)
 *
 * Zeigt eine Karte, wenn eine Session automatisch abgeschlossen wurde
 * (Handy aus, vergessen zu beenden) und der Logbucheintrag noch ein
 * Entwurf ist. Klick führt zum nachträglichen Feedback-Formular.
 *
 * Selbstladend (GET /learning-journal/drafts), dismissbar pro Sitzung
 * (sessionStorage) - beim nächsten App-Start erscheint sie wieder,
 * bis der Eintrag vervollständigt wurde.
 */
const DISMISS_KEY = 'dismissed-draft-banner';

const DraftFeedbackBanner = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    const loadDrafts = async () => {
      try {
        const response = await apiService.getLearningJournalDrafts();
        if (!cancelled && response?.success && Array.isArray(response.data)) {
          // Nur automatisch abgeschlossene Sessions bewerben
          setDrafts(response.data.filter(d => d.autoCompleted));
        }
      } catch (error) {
        // Kein Banner ist kein Drama - still scheitern
      }
    };
    loadDrafts();
    return () => { cancelled = true; };
  }, []);

  const visibleDrafts = drafts.filter(d => !dismissed.includes(d.sessionId));
  if (visibleDrafts.length === 0) return null;

  const draft = visibleDrafts[0];

  const handleDismiss = () => {
    const next = [...dismissed, draft.sessionId];
    setDismissed(next);
    try {
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch {
      // sessionStorage nicht verfügbar - Banner bleibt halt
    }
  };

  return (
    // `border-gray-200/80 dark:border-white/10` war ein Literal-plus-dark:-Paar -
    // genau das Muster, das TOKENS.md durch ein einzelnes `border-border` ersetzt.
    <div className="mb-4 rounded-xl border border-border bg-surface/80 backdrop-blur-md shadow-token-sm overflow-hidden animate-fade-in">
      <div className="p-4 flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-surface-raised flex items-center justify-center">
          <BookOpen className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground">
            Deine Session „{draft.topic || 'Lernsession'}“ wurde automatisch abgeschlossen
          </p>
          <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
            Vervollständige deinen Logbucheintrag
            {draft.hasAiDraft && (
              <span className="inline-flex items-center gap-1 text-foreground/80 font-medium">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                TeachOtter hat einen Entwurf vorbereitet
              </span>
            )}
          </p>
          {visibleDrafts.length > 1 && (
            <p className="text-xs text-muted-foreground/70 mt-1">
              +{visibleDrafts.length - 1} weitere Session{visibleDrafts.length > 2 ? 's' : ''} wartet auf dich
            </p>
          )}
          <Button
            size="sm"
            onClick={() => navigate(`/session/${draft.sessionId}/feedback`)}
            className="mt-3"
          >
            Jetzt vervollständigen
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="shrink-0 px-2"
          aria-label="Hinweis schließen"
        >
          <X className="h-4 w-4 text-muted-foreground/70" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};

export default DraftFeedbackBanner;
